import { Checkbox } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { DocTypeFiles } from 'shared/constants/docs';
import { useProjectContext } from 'shared/_pages/project/provider';

type Props = {
	type?: DocTypeFiles;
};

/**
 * Checkbox of a file group header: checked when all files of the group are selected, indeterminate when
 * only some are. Click selects all files of the group (all project files when no type is given), or
 * unselects them when they are all selected.
 */
export default function SelectFilesButton({ type }: Props) {
	const { files, selectedFileIds, setSelectedFileIds } = useProjectContext();

	const groupFileIds = useMemo(() => {
		return (type ? files.filter((f) => f.type === type) : files).map((f) => f._id);
	}, [files, type]);

	const selectedCount = groupFileIds.filter((id) => selectedFileIds.includes(id)).length;
	const isAllSelected = groupFileIds.length > 0 && selectedCount === groupFileIds.length;
	const isSomeSelected = selectedCount > 0 && !isAllSelected;
	const label = isAllSelected ? 'Unselect all' : 'Select all';

	const handleClick = useCallback(() => {
		setSelectedFileIds(groupFileIds, !isAllSelected);
	}, [setSelectedFileIds, groupFileIds, isAllSelected]);

	return (
		<Checkbox
			size="small"
			checked={isAllSelected}
			indeterminate={isSomeSelected}
			disabled={!groupFileIds.length}
			onChange={handleClick}
			title={label}
			slotProps={{ input: { 'aria-label': label } }}
		/>
	);
}
